import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  Target,
  ShieldCheck,
  Zap,
  Building2,
  Rocket,
  Hammer,
  Layers,
  Briefcase,
  Handshake,
  Mail,
  MapPin,
  Sparkles,
  Globe
} from "lucide-react";
import Navbar from "../Components/Navbar";
import Call from "../Components/Call";
import Footer from "../Components/Footer";
import "../Styling/About.css";
import ChatbotWidget from "./ChatComponent";

gsap.registerPlugin(ScrollTrigger);

const containerVariants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0,
      delayChildren: 0
    }
  }
};

const itemVariants = {
  hidden: { opacity: 1, y: 0 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0 }
  }
};


const mapLocations = {
  us: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3527.1436423985!2d-82.6393333!3d27.8446055!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88c2e646736484e9%3A0x6d97c7b8098000!2s7901+4th+St+N%2C+St.+Petersburg%2C+FL+33702!5e0!3m2!1sen!2sus!4v1706310000000!5m2!1sen!2sus",
  it: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2971.0457613583!2d12.6366573!3d41.8596645!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132f7a07fc29094d%3A0x958178e69d7bdf9!2sVia+Tersicore%2C+00133+Roma+RM%2C+Italy!5e0!3m2!1sen!2sus!4v1706310000000!5m2!1sen!2sus"
};

export default function About() {
  const [activeMap, setActiveMap] = React.useState("us");
  const mainRef = React.useRef(null);

  React.useEffect(() => {
    const ctx = gsap.context((self) => {
      // Hero Visual Entrance - instant
      gsap.set(".About-hero-visual > *", {
        opacity: 1,
        y: 0,
        scale: 1
      });

      // Background Blobs
      gsap.to(".About-blob", {
        x: "random(-100, 100)",
        y: "random(-100, 100)",
        duration: 10,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        stagger: 0.5
      });

      // Mesh Overlay Rotation
      gsap.to(".About-mesh-overlay", {
        rotation: 360,
        duration: 120,
        repeat: -1,
        ease: "none"
      });

      // Show all sections instantly
      const sections = self.selector('section');
      sections.forEach(section => {
        gsap.set(section, { opacity: 1, y: 0 });
      });

      ScrollTrigger.refresh();
    }, mainRef);

    const refreshTimer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 500);

    return () => {
      ctx.revert();
      clearTimeout(refreshTimer);
    };
  }, []);

  return (
    <div className="About-page-wrapper" ref={mainRef}>
      <Navbar />

      {/* Cinematic Background Elements */}
      <div className="About-mesh-container">
        <div className="About-mesh-overlay"></div>
        <div className="About-blob blob-purple"></div>
        <div className="About-blob blob-blue"></div>
        <div className="About-blob blob-cyan"></div>
      </div>

      {/* Hero Section */}
      <section className="About-header-section">
        <div className="container">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.span
              variants={itemVariants}
              className="About-badge"
              whileHover={{ scale: 1.05, boxShadow: "0 8px 15px rgba(91, 76, 255, 0.15)" }}
            >
              <Sparkles size={14} /> Who We Are
            </motion.span>

            <motion.h1 variants={itemVariants} className="About-header-title">
              About Digital Dream Works
            </motion.h1>

            <motion.p variants={itemVariants} className="About-header-desc">
              Digital Dream Works is an AI and Software Studio that engineers production-ready systems for agencies, enterprises, and strategic partners. We design, build, and deploy AI-driven platforms that replace manual operations, fragmented tooling, and slow execution with scalable, secure systems used in real business environments.
              <br /><br />
              We do not sell templates, tools, or experiments. Every system we deploy is engineered for production use, long-term operation, and commercial impact.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section - Using Local Image */}
      <section className="About-mission-section">
        <div className="container">
          <div className="About-mission-split">
            <motion.div
              className="About-mission-image-wrapper"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
            >
              <img
                src="/images/about/Our Mission.png"
                alt="Digital Dream Works Studio"
                className="About-real-image"
              />
            </motion.div>

            <motion.div
              className="About-mission-box"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
            >
              <div className="About-mission-content">
                <h2 className="About-mission-title">Our Mission</h2>
                <p className="About-mission-statement">
                  Digital Dream Works exists to eliminate the gap between business requirements and technical execution.
                </p>
                <p className="About-mission-desc">
                  Modern organizations struggle with disconnected tools, manual workflows, and systems that never move beyond prototypes. Our mission is to design AI-powered systems that work immediately in production, integrate into existing operations, and deliver measurable operational leverage from day one.
                  <br /><br />
                  We focus on execution, reliability, and real-world deployment, not theoretical innovation.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Operating Principles Section */}
      <section className="About-principles-section">
        <div className="container">
          <div className="About-section-header">
            <h2 className="About-principles-title">Operating Principles</h2>
            <div className="About-title-underline" style={{ width: '60px' }} />
          </div>

          <div className="About-principles-grid">
            {[
              { icon: Target, title: "Built for Production", desc: "Every system we engineer is designed for live environments, real users, and ongoing operation. We do not build demos or MVPs that fail under scale." },
              { icon: ShieldCheck, title: "Enterprise-Grade Security", desc: "Security, compliance, and data protection are treated as foundational requirements. Our systems are built with production security standards suitable for enterprise deployment." },
              { icon: Zap, title: "Speed to Market", desc: "We deploy production-ready systems in weeks, not months, without compromising reliability, scalability, or long-term maintainability." }
            ].map((principle, idx) => (
              <motion.div
                key={idx}
                className="About-principle-card"
                whileHover={{ y: -10, boxShadow: "0 25px 50px rgba(0,0,0,0.05)" }}
              >
                <div className="About-principle-icon-wrapper">
                  <principle.icon className="About-principle-icon" />
                </div>
                <h3 className="About-principle-title">{principle.title}</h3>
                <p className="About-principle-desc">{principle.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Differences Section - Using Local Image */}
      <section className="About-differences-section">
        <div className="container">
          <div className="About-differences-layout">
            <motion.div
              className="About-differences-box"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <div className="About-section-header">
                <h2 className="About-differences-title">How We're Different</h2>
                <div className="About-title-underline" style={{ width: '60px' }} />
              </div>

              <div className="About-differences-grid">
                {[
                  { icon: Building2, title: "Not a consultancy", desc: "We do not sell strategy decks or advisory hours. We engineer and deploy working systems." },
                  { icon: Hammer, title: "Not a development shop", desc: "We do not execute specs handed down to us. We design systems end-to-end based on operational needs." },
                  { icon: Layers, title: "Not a SaaS product", desc: "Our systems are customized to your workflows, data, and business logic." },
                  { icon: Rocket, title: "Not an agency", desc: "We do not run campaigns or one-off projects. We deploy systems that run operations." }
                ].map((diff, idx) => (
                  <motion.div key={idx} className="About-difference-item" whileHover={{ x: 8 }}>
                    <diff.icon className="About-diff-icon" size={24} />
                    <div className="About-diff-text">
                      <h4 className="About-difference-title">{diff.title}</h4>
                      <p className="About-difference-desc">{diff.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="About-diff-image-side"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1 }}
            >
              <img
                src="/images/about/How we are different.png"
                alt="AI Robotics and Systems Engineering"
                className="About-real-image diff-img"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who We Work With Section */}
      <section className="About-clients-section">
        <div className="container">
          <div className="About-section-header">
            <h2 className="About-clients-title">Who We Work With</h2>
            <div className="About-title-underline" style={{ width: '60px' }} />
          </div>

          <div className="About-clients-grid">
            {[
              { icon: Briefcase, title: "Agencies", desc: "Agencies use our systems to scale delivery, automate internal operations, and launch white-label solutions without building internal R&D teams." },
              { icon: Building2, title: "Enterprises", desc: "Enterprises partner with us to deploy production-grade AI systems without long R&D cycles, vendor lock-in, or fragile tooling." },
              { icon: Handshake, title: "Strategic Partners", desc: "Strategic partners work with us to launch new offerings, co-develop platforms, and deploy systems without infrastructure risk." }
            ].map((client, idx) => (
              <motion.div key={idx} className="About-client-card" whileHover={{ y: -5 }}>
                <div className="About-client-icon"><client.icon size={28} /></div>
                <h4 className="About-client-card-title">{client.title}</h4>
                <p className="About-client-card-desc">{client.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Info Section */}
      <section className="About-company-section">
        <div className="container">
          <motion.div className="About-company-box">
            <div className="About-section-header" style={{ marginBottom: '2rem' }}>
              <h2 className="About-company-title">Company Information</h2>
              <div className="About-title-underline" style={{ width: '60px' }} />
            </div>

            <p style={{ textAlign: 'center', color: 'var(--about-text-muted)', marginBottom: '3rem', fontSize: '0.95rem' }}>
              Digital Dream Works operates internationally, supporting clients across Europe, the United States, and global markets.
            </p>

            <div className="About-company-flex">
              <div className="About-company-info-side">
                <div className={`About-company-item ${activeMap === "us" ? "active-location" : ""}`} onClick={() => setActiveMap("us")} style={{ cursor: 'pointer' }}>
                  <div className="About-company-icon-box"><MapPin size={22} color={activeMap === "us" ? "var(--about-primary)" : "currentColor"} /></div>
                  <div className="About-company-text">
                    <p className="About-label">USA HQ</p>
                    <p className="About-company-name">Digital Dream Works LLC</p>
                    <p>ST PETERSBURG, FL 33702</p>
                  </div>
                </div>

                <div className={`About-company-item ${activeMap === "it" ? "active-location" : ""}`} onClick={() => setActiveMap("it")} style={{ cursor: 'pointer' }}>
                  <div className="About-company-icon-box"><Globe size={22} color={activeMap === "it" ? "var(--about-primary)" : "currentColor"} /></div>
                  <div className="About-company-text">
                    <p className="About-label">EU Office</p>
                    <p className="About-company-name">Digital Dream Works Agency</p>
                    <p>00133 Roma RM, Italy</p>
                  </div>
                </div>

                <div className="About-company-item">
                  <div className="About-company-icon-box"><Mail size={22} /></div>
                  <div className="About-company-text">
                    <p className="About-label">Support</p>
                    <p>Hello@digitaldreamworksagency.com</p>
                  </div>
                </div>
              </div>

              <div className="About-company-map-side">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeMap}
                    initial={{ opacity: 0, filter: "blur(10px)" }}
                    animate={{ opacity: 1, filter: "blur(0px)" }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    style={{ height: '100%' }}
                  >
                    <iframe title="Location" src={mapLocations[activeMap]} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" style={{ borderRadius: '1.2rem', border: 'none', width: '100%', height: '100%' }}></iframe>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <ChatbotWidget />
      <Call />
      <Footer />
    </div>
  );
}

